import { type StoryblokRichTextNode, useStoryblokServerRichText } from "@storyblok/react/rsc";
import {
	Children,
	type CSSProperties,
	cloneElement,
	isValidElement,
	type ReactElement,
	type ReactNode,
} from "react";
import type { RichtextStoryblok } from "@/generated/sb/sb-types";

/**
 * Render a Storyblok richtext document, returning `undefined` if empty.
 * Share this across bloks so richtext rendering stays consistent.
 *
 * "Empty" includes docs the editor leaves behind when a field is touched but
 * never typed in — e.g. `{ type: "doc", content: [{ type: "paragraph" }] }`.
 * Returning undefined for these lets callers reliably gate rendering on
 * `body && (...)`.
 *
 * Uses the resolver hook directly (not <StoryblokServerRichText/>) so the
 * paragraphs become direct children of any flex container wrapping them —
 * the SDK component wraps its output in an extra <div> which would
 * collapse `gap` spacing in `<Prose>` and make single line breaks invisible.
 */
export const renderRichText = (doc: RichtextStoryblok | undefined) => {
	// Not actually a React hook — it's a resolver factory (returns `{ render }`)
	// despite the `use*` name. Called unconditionally to satisfy lint rules and
	// to give each call its own fresh key counter.
	const { render } = useStoryblokServerRichText({});
	if (!doc || isEmptyRichText(doc)) return undefined;
	const rendered = render(doc as unknown as StoryblokRichTextNode<ReactElement>);
	return <>{normalizeAttrs(rendered)}</>;
};

/**
 * Convert HTML attribute names produced by the Storyblok richtext resolver
 * (`class`, `for`, inline style strings, etc.) to the React equivalents. The
 * SDK's <StoryblokServerRichText/> component does this internally via its
 * `convertAttributesInElement` helper; we replicate it here because we call
 * the resolver hook directly to skip the wrapping <div>.
 */
const HTML_TO_REACT_ATTR: Record<string, string> = {
	allowfullscreen: "allowFullScreen",
	autocomplete: "autoComplete",
	autofocus: "autoFocus",
	autoplay: "autoPlay",
	charset: "charSet",
	class: "className",
	colspan: "colSpan",
	colwidth: "colWidth",
	contenteditable: "contentEditable",
	crossorigin: "crossOrigin",
	enctype: "encType",
	for: "htmlFor",
	formnovalidate: "formNoValidate",
	frameborder: "frameBorder",
	inputmode: "inputMode",
	marginheight: "marginHeight",
	marginwidth: "marginWidth",
	maxlength: "maxLength",
	minlength: "minLength",
	novalidate: "noValidate",
	playsinline: "playsInline",
	readonly: "readOnly",
	referrerpolicy: "referrerPolicy",
	rowspan: "rowSpan",
	srcset: "srcSet",
	tabindex: "tabIndex",
	usemap: "useMap",
};

const parseStyleString = (style: string): CSSProperties => {
	const result: Record<string, string> = {};
	for (const rule of style.split(";")) {
		const [rawProp, rawValue] = rule.split(":");
		const prop = rawProp?.trim();
		const value = rawValue?.trim();
		if (!prop || !value) continue;
		const camel = prop.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
		result[camel] = value;
	}
	return result as CSSProperties;
};

const normalizeAttrs = (node: ReactNode): ReactNode => {
	if (Array.isArray(node)) return node.map(normalizeAttrs);
	if (!isValidElement(node)) return node;
	const element = node as ReactElement<Record<string, unknown>>;
	const nextProps: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(element.props)) {
		if (key === "children") continue;
		const mappedKey = HTML_TO_REACT_ATTR[key] ?? key;
		nextProps[mappedKey] =
			key === "style" && typeof value === "string" ? parseStyleString(value) : value;
	}
	const children = Children.map(element.props.children as ReactNode, (child) =>
		typeof child === "string" ? child : normalizeAttrs(child),
	);
	return cloneElement(element, nextProps, children);
};

const isEmptyRichText = (doc: RichtextStoryblok): boolean => {
	const content = (doc as { content?: unknown[] }).content;
	if (!Array.isArray(content) || content.length === 0) return true;
	return content.every(isEmptyNode);
};

const isEmptyNode = (node: unknown): boolean => {
	if (!node || typeof node !== "object") return true;
	const n = node as { type?: string; text?: string; content?: unknown[] };
	if (n.type === "text") return !n.text;
	if (Array.isArray(n.content)) return n.content.every(isEmptyNode);
	return true;
};
